import { Box, Typography } from '@mui/material'
import { alpha, useTheme } from '@mui/material/styles'
import { useTranslation } from 'react-i18next'

export type BillingPeriod = {
  /** Months one purchase covers. The value the cards are keyed on. */
  months: number
  /** How much cheaper the month is here than on the monthly period. */
  discountPercent: number
}

/**
 * How often you pay — one control for the whole price list.
 *
 * One, not one per card. Three toggles that had to be moved in sync said the
 * choice belonged to each plan when it belongs to the page, and a customer
 * comparing 99 against 899 had to remember to flip both before the comparison
 * meant anything.
 *
 * The selected segment is a moving pill rather than a restyled button, so the
 * eye follows the selection across instead of re-finding it. It travels on a
 * critically damped curve — no overshoot, because nothing was thrown here;
 * a press is not a flick.
 */
export default function BillingPeriodToggle({
  periods,
  selectedMonths,
  onSelect,
  disabled = false,
}: {
  periods: BillingPeriod[]
  selectedMonths: number
  onSelect: (months: number) => void
  disabled?: boolean
}) {
  const theme = useTheme()
  const c = theme.affexy.colors
  const { t } = useTranslation('plans')

  if (periods.length < 2) return null

  const index = Math.max(
    0,
    periods.findIndex((period) => period.months === selectedMonths),
  )
  const selected = periods[index]

  return (
    <Box sx={{ display: 'grid', justifyItems: 'center', gap: 1 }}>
      <Box
        role="radiogroup"
        aria-label={t('packages.legend')}
        sx={{
          position: 'relative',
          display: 'grid',
          gridAutoFlow: 'column',
          gridAutoColumns: '1fr',
          p: 0.5,
          borderRadius: `${theme.affexy.radii.pill}px`,
          bgcolor: c.gray100,
          opacity: disabled ? 0.6 : 1,
        }}
      >
        {/* The pill sits under the labels and moves between them. Transform
            only, so it composites and never reflows the row it sits in. */}
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            top: 4,
            bottom: 4,
            left: 4,
            width: `calc((100% - 8px) / ${periods.length})`,
            transform: `translateX(${index * 100}%)`,
            borderRadius: `${theme.affexy.radii.pill}px`,
            bgcolor: c.white,
            boxShadow: '0 1px 3px rgba(27,28,56,.10)',
            transition: 'transform 340ms cubic-bezier(0.32, 0.72, 0, 1)',
            '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
          }}
        />
        {periods.map((period) => {
          const active = period.months === selectedMonths
          return (
            <Box
              key={period.months}
              component="button"
              type="button"
              role="radio"
              aria-checked={active}
              disabled={disabled}
              onClick={() => onSelect(period.months)}
              sx={{
                position: 'relative',
                display: 'flex',
                alignItems: 'baseline',
                justifyContent: 'center',
                gap: 0.75,
                border: 0,
                background: 'none',
                cursor: disabled ? 'default' : 'pointer',
                px: 2.5,
                py: 0.9,
                borderRadius: `${theme.affexy.radii.pill}px`,
                font: 'inherit',
                fontSize: 13.5,
                fontWeight: active ? 700 : 500,
                color: active ? c.ink : c.ink56,
                whiteSpace: 'nowrap',
                // Feedback on the press itself, not on the result of it.
                transition: 'color 200ms ease, transform 100ms ease-out',
                '&:active': { transform: disabled ? 'none' : 'scale(0.98)' },
              }}
            >
              {period.months === 1
                ? t('packages.billedMonthly')
                : t('packages.billedEvery', { count: period.months })}
              {period.discountPercent > 0 && (
                <Typography
                  component="span"
                  sx={{
                    fontSize: 11.5,
                    fontWeight: 700,
                    color: active ? c.coral : alpha(c.coral, 0.7),
                  }}
                >
                  {t('packages.discount', { percent: period.discountPercent })}
                </Typography>
              )}
            </Box>
          )
        })}
      </Box>

      {/* Said once for the page, not three times down the column. The row keeps
          its height whichever period is selected, so the cards never jump. */}
      <Typography sx={{ fontSize: 12.5, color: c.ink48, minHeight: 18, textAlign: 'center' }}>
        {selected && selected.months > 1 ? t('packages.chargedUpFront', { count: selected.months }) : ''}
      </Typography>
    </Box>
  )
}
